import type { ReactNode } from 'react'
import type { Theme } from '@mui/material/styles'
import { endOfMonth, startOfMonth } from 'date-fns'
import { getCategoryColor } from '../../../core/utils/CategoryUtil'
import {
  type Beat,
  type InsightsFeedCopy,
  type Subtitle,
  type Transaction,
  buildDynamicCopy,
} from '../../../core'
import type { TransactionFilter } from '../../../core/stores/TransactionStore'
import type { InsightsInstanceSlotData } from '../../../core/types/InsightsInstanceSlotData'
import { MerchantLogo } from '@mxenabled/mxui'
import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { Icon } from '@mxenabled/mx-icons'
import type { ChartDataSeriesItem } from '../../blocks/ChartBlocks/BarchartBlock/utils'
import type { TipSectionProps } from '../../../insights/components/shared/ChartDrawerTemplate/TipSection'

export interface DrillDownHeader {
  heading?: string
  subHeadingText?: string
  icon?: ReactNode
}

export interface DrillDownHeaderContext {
  title?: string
  subHeader?: Subtitle
  categoryName: string
  selectedDate?: string | Date
}

export const DRILLDOWN_VARIANTS = {
  TRANSACTION: 'bill_amount_not_standard',
  CATEGORY_SPEND: 'category_spend',
  MONTHLY_SPEND: 'monthly_spend_comparison',
  ACCOUNT_BALANCE: 'savings_account_balances_summary',
  UPCOMING_BILL: 'upcoming_bill',
  SPEND_TO_INCOME: 'monthly_spend_to_income',
  SAVINGS_TO_INCOME: 'monthly_savings_to_income',
  DEBT_TO_INCOME: 'monthly_debt_to_income',
  COST_OF_LIVING: 'cost_living_to_income',
  NON_ESSENTIAL_CATEGORY: 'non_essential_category_summary',
  DEPOSIT_SUMMARY: 'monthly_deposit_summary',
} as const

export type DrillDownVariant = (typeof DRILLDOWN_VARIANTS)[keyof typeof DRILLDOWN_VARIANTS]

/**
 * One row of the flattened data_series a filterStrategy indexes into: a single-bar monthly total or a
 * synthesized upcoming-bill bar. Fields are optional because the backend keys each row dynamically
 * from data_series.names and not every variant carries a guid/category. Double-bar comparison rows
 * will extend this in the next MR.
 */

// Resolves the top-chart palette from the theme (and the beat's category, when relevant).
export type ColorKind = (theme: Theme, categoryGuid?: string) => string[]

// Resolves the dynamic drawer header (heading/sub-heading/icon JSX) from the selection context.
export type HeaderKind = (ctx: DrillDownHeaderContext) => DrillDownHeader

/**
 * Everything a filterStrategy needs to turn the currently-selected bar/sub-tab into a
 * TransactionFilter. Data-only so strategies stay pure and testable.
 */
export interface FilterContext {
  beat: Beat
  beatTransactions: Transaction[]
  dataSeries: ChartDataSeriesItem[]
  selectedIndex: number
  subIndex: number
  isLastTab: boolean
  instanceSlotData: InsightsInstanceSlotData
  spendCategoryGuids: string[]
  displayedDateRange: { start: Date; end: Date }
}

// Resolves the current selection into a TransactionFilter. Carried on each config row so behavior
// lives next to the variant it belongs to (no separate strategy registry to chase).
export type FilterStrategy = (ctx: FilterContext) => TransactionFilter

export interface DrillDownVariantConfig {
  top: 'singleBar' | 'upcomingBill' | 'doubleBar' | 'tabsOnly'
  filterStrategy: FilterStrategy
  colors: ColorKind
  header?: HeaderKind
  tipSection?: (
    setCloseTipSection: (flag: boolean) => void,
    copy: InsightsFeedCopy,
    merchantName?: string,
  ) => TipSectionProps
  scope: 'store' | 'beat'
  filterMode: 'store' | 'local'
}
const categoryColor = (theme: Theme, categoryGuid?: string): string =>
  (categoryGuid && getCategoryColor(categoryGuid, theme)) || theme.palette.primary.main

const monthRange = (date: string | number | Date | undefined) => ({
  start: startOfMonth(new Date(date ?? NaN)),
  end: endOfMonth(new Date(date ?? NaN)),
})

// Scopes a filter to the beat's accounts when the beat carries account_guids; otherwise leaves
// accounts unset so the store default (all accounts) applies.
const accountScope = (accountGuids?: string[]): Pick<TransactionFilter, 'accounts'> =>
  accountGuids?.length ? { accounts: accountGuids } : {}

// ── Single-bar filter strategies (this MR) ────────────────────────────────────────────────────

// BillAmountNotStandard / UpcomingBill — the series carries a transaction (TRN) guid; match a single
// transaction by guid within its month.
const transactionGuidStrategy: FilterStrategy = ({
  beatTransactions,
  dataSeries,
  selectedIndex,
}) => {
  const seriesItem = dataSeries[selectedIndex]
  const seriesGuid = seriesItem?.guid
  const hasBeenSplit = beatTransactions.find((trn) => trn.guid === seriesGuid)?.has_been_split

  return {
    custom: (trn) => trn.guid === seriesGuid,
    dateRange: monthRange(seriesItem?.date),
    showSplits: !hasBeenSplit,
  }
}

// CategorySpendingV2 — match transactions in the beat's category (or whose top-level category is that
// guid) within the month.
const categoryStrategy: FilterStrategy = ({ dataSeries, selectedIndex, instanceSlotData }) => {
  const seriesItem = dataSeries[selectedIndex]
  const categoryGuid = instanceSlotData.category_guid ?? seriesItem?.guid

  return {
    custom: (trn) =>
      trn.category_guid === categoryGuid || trn.top_level_category_guid === categoryGuid,
    dateRange: monthRange(seriesItem?.date),
  }
}

// MonthlySpendComparisonV2 — no per-bar guid; show all spending-category transactions
// (income/transfers/investments excluded via spendCategoryGuids) within the month.
const spendCategoriesStrategy: FilterStrategy = ({
  dataSeries,
  selectedIndex,
  spendCategoryGuids,
  instanceSlotData,
}) => {
  const seriesItem = dataSeries[selectedIndex]

  return {
    custom: (trn) =>
      !!trn.top_level_category_guid && spendCategoryGuids.includes(trn.top_level_category_guid),
    dateRange: monthRange(seriesItem?.date),
    ...accountScope(instanceSlotData.account_guids),
  }
}

// SavingsAccountBalancesSummary — balances, not categorized spend. Filter by month only; the most
// recent month is capped at the beat's creation time to match the snapshot the balance was computed
// from.
const accountDateStrategy: FilterStrategy = ({
  beat,
  dataSeries,
  selectedIndex,
  isLastTab,
  instanceSlotData,
}) => {
  const seriesItem = dataSeries[selectedIndex]
  const { start, end } = monthRange(seriesItem?.date)

  return {
    dateRange: {
      start,
      end: isLastTab && beat.created_at ? new Date(beat.created_at) : end,
    },
    ...accountScope(instanceSlotData.account_guids),
  }
}

// ── Double-bar + tabsOnly strategies (NEXT MR) ───────────────────────────────────────────────────
// TODO(next MR — double bar): comparisonAggregatedGuidsStrategy — month × sub-tab membership over the
// comparison bars' precomputed subGuids. Needs comparisonBars in FilterContext.
// TODO(next MR — tabsOnly): aggregatedGuids / depositSummary strategies for the donut-drawer beats.

export const layout: Partial<Record<DrillDownVariant, DrillDownVariantConfig>> = {
  [DRILLDOWN_VARIANTS.TRANSACTION]: {
    top: 'singleBar',
    scope: 'store',
    filterMode: 'store',
    filterStrategy: transactionGuidStrategy,
    colors: (theme: Theme, categoryGuid?: string) => [categoryColor(theme, categoryGuid)],
    header: (ctx: DrillDownHeaderContext) => {
      const { title, subHeader } = ctx
      return {
        heading: title,
        subHeadingText: subHeader?.text || undefined,
        icon: (
          <MerchantLogo
            alt={subHeader?.text}
            categoryGuid={subHeader?.logo?.category_guid || ''}
            merchantGuid={subHeader?.logo?.merchant_guid || ''}
            size={16}
          />
        ),
      }
    },
    tipSection: (setCloseTipSection, copy, merchantName = '') => {
      return {
        message: buildDynamicCopy(
          copy.general.tip_section_message,
          <strong>{copy.general.tip_section_title}</strong>,
          <strong>{merchantName}</strong>,
        ),
        onDismiss: () => setCloseTipSection(true),
      } as TipSectionProps
    },
  },
  [DRILLDOWN_VARIANTS.CATEGORY_SPEND]: {
    top: 'singleBar',
    filterStrategy: categoryStrategy,
    scope: 'store',
    filterMode: 'store',
    colors: (theme: Theme, categoryGuid?: string) => [categoryColor(theme, categoryGuid)],
    header: (ctx: DrillDownHeaderContext) => {
      return {
        heading: ctx.categoryName,
        subHeadingText: ctx.selectedDate
          ? formatDate(ctx.selectedDate, DATE_FORMATS_INTL.MONTH_LONG) || ''
          : '',
        icon: <Icon name="calendar_month" size={16} />,
      }
    },
  },
  [DRILLDOWN_VARIANTS.MONTHLY_SPEND]: {
    top: 'singleBar',
    filterStrategy: spendCategoriesStrategy,
    scope: 'store',
    filterMode: 'store',
    colors: (theme: Theme) => [theme.palette.chart.chart2 ?? theme.palette.primary.main],
  },
  [DRILLDOWN_VARIANTS.ACCOUNT_BALANCE]: {
    top: 'singleBar',
    filterStrategy: accountDateStrategy,
    scope: 'store',
    filterMode: 'store',
    colors: (theme: Theme) => [theme.palette.success.main],
  },
  [DRILLDOWN_VARIANTS.UPCOMING_BILL]: {
    top: 'upcomingBill',
    filterStrategy: transactionGuidStrategy,
    scope: 'beat',
    filterMode: 'local',
    colors: (theme: Theme, categoryGuid?: string) => [categoryColor(theme, categoryGuid)],
  },

  // ── NEXT MR: double-bar comparison beats (month tabs + spend/income sub-tabs) ────────────────
  // [DRILLDOWN_VARIANTS.SPEND_TO_INCOME]:   comparison('spendVsIncome'),
  // [DRILLDOWN_VARIANTS.SAVINGS_TO_INCOME]: comparison('savingsVsIncome'),
  // [DRILLDOWN_VARIANTS.DEBT_TO_INCOME]:    comparison('debtVsIncome'),
  // [DRILLDOWN_VARIANTS.COST_OF_LIVING]:    comparison('costOfLiving'),
  //
  // ── NEXT MR: tabsOnly donut-drawer beats (no top chart) ───────────────────────────────────
  // [DRILLDOWN_VARIANTS.NON_ESSENTIAL_CATEGORY]: { top: 'tabsOnly', ... },
  // [DRILLDOWN_VARIANTS.DEPOSIT_SUMMARY]:        { top: 'tabsOnly', ... },
}

// Fallback for a missing/typo'd backend variant: a single-bar, store-scoped drawer filtered by the
// beat's transaction guid and colored by category, so the drawer still renders instead of crashing.
export const FALLBACK: DrillDownVariantConfig = {
  top: 'singleBar',
  filterStrategy: transactionGuidStrategy,
  scope: 'store',
  filterMode: 'store',
  colors: (theme: Theme, categoryGuid?: string) => [categoryColor(theme, categoryGuid)],
}
